import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { registerClient, getClientsByRut } from '../api';
import { CLIENT_TYPE_OPTIONS } from '../utils/clientType';

const ModalClientRegistration = ({
    isOpen,
    onClose,
    name,
    setName,
    searchInputRef,
    setClientId,
    rut,
    setRut,
    email,
    setEmail,
    address,
    setAddress,
    city,
    setCity,
    phone,
    setPhone,
    savePurchase,
    setModalVarietyPrice,
    allowContinueWithoutClient = false,
    clientType,
    setClientType,
    onClientSelected,
    requireClient = false,
    clientTypeOptions = CLIENT_TYPE_OPTIONS,
    canCreateClient = true,
    canReadClient = true,
    detailActionLabel = 'Venta Detalle',
    cancelLabel = 'Deshacer Venta',
    clientSelectionHandlesPurchase = false,
}) => {
    const [showFields, setShowFields] = useState(false);
    const navigate = useNavigate();
    const branchId = useParams().branchId || localStorage.getItem('selectedBranchId');
    const [typeError, setTypeError] = useState('');
    const [selectingTypeOnly, setSelectingTypeOnly] = useState(false);
    const [selectionInFlight, setSelectionInFlight] = useState(false);
    const [registrationInFlight, setRegistrationInFlight] = useState(false);
    const searchDebounceRef = useRef(null);
    const lookupRequestIdRef = useRef(0);
    const lastLookupRutRef = useRef('');
    const selectionInFlightRef = useRef(false);
    const registrationInFlightRef = useRef(false);
    const wasOpenRef = useRef(false);
    const committedClientFieldsRef = useRef(null);
    const resolvedClientTypeOptions =
        Array.isArray(clientTypeOptions) && clientTypeOptions.length
            ? clientTypeOptions
            : CLIENT_TYPE_OPTIONS;

    const formatRut = (e) => {
        setRut(e.target.value
            .replace(/[^0-9kK]/g, '')
            .replace(/^(\d{1,2})(\d{3})(\d{3})([0-9kK])$/, '$1.$2.$3-$4')
            .toUpperCase())
    }

    const normalizeRutForLookup = (value) =>
        String(value || '').replace(/[^0-9kK]/g, '').toUpperCase();

    const restoreCommittedClientFields = useCallback(() => {
        const snapshot = committedClientFieldsRef.current;
        if (!snapshot) return;
        setName(snapshot.name);
        setRut(snapshot.rut);
        setEmail(snapshot.email);
        setAddress(snapshot.address);
        setCity(snapshot.city);
        setPhone(snapshot.phone);
    }, [setAddress, setCity, setEmail, setName, setPhone, setRut]);

    useEffect(() => {
        if (isOpen && !wasOpenRef.current) {
            committedClientFieldsRef.current = {
                name,
                rut,
                email,
                address,
                city,
                phone,
            };
        }
        wasOpenRef.current = isOpen;
    }, [address, city, email, isOpen, name, phone, rut]);

    const applyClientSelection = useCallback(async (client, options = {}) => {
        const { source = 'manual' } = options;
        if (!client) return false;
        if (selectionInFlightRef.current) return false;
        selectionInFlightRef.current = true;
        setSelectionInFlight(true);
        let persistenceSucceeded = false;
        try {
            if (clientSelectionHandlesPurchase) {
                if (source === 'auto' || source === 'auto-clear' || !onClientSelected) return false;
                const handled = Boolean(await onClientSelected(client, { source }));
                if (!handled) {
                    restoreCommittedClientFields();
                    return false;
                }
                persistenceSucceeded = true;
                return true;
            }
            if (typeof savePurchase === 'function') {
                const saveResult = await savePurchase(client.id);
                if (!saveResult) {
                    restoreCommittedClientFields();
                    return false;
                }
            }
            persistenceSucceeded = true;
            committedClientFieldsRef.current = {
                name: client.name || '',
                rut: client.rut || '',
                email: client.email || '',
                address: client.address || '',
                city: client.city || '',
                phone: client.phone || '',
            };
            setName(client.name || '');
            setEmail(client.email || '');
            setAddress(client.address || '');
            setCity(client.city || '');
            setPhone(client.phone || '');
            setRut(client.rut || '');
            setClientId(client.id);
            if (onClientSelected) {
                await onClientSelected(client, { source });
            }
            setShowFields(false);
            return true;
        } catch (error) {
            if (!persistenceSucceeded) restoreCommittedClientFields();
            throw error;
        } finally {
            selectionInFlightRef.current = false;
            setSelectionInFlight(false);
        }
    }, [
        clientSelectionHandlesPurchase,
        onClientSelected,
        restoreCommittedClientFields,
        savePurchase,
        setAddress,
        setCity,
        setClientId,
        setEmail,
        setName,
        setPhone,
        setRut,
    ]);

    const lookupClientByRut = useCallback(async (rutValue, options = {}) => {
        if (!canReadClient) return false;
        const {
            showRegisterFieldsOnMiss = true,
            closeOnFound = false,
            clearSelectionOnMiss = false,
            lookupSource = 'manual-search',
        } = options;
        const normalizedRut = normalizeRutForLookup(rutValue);
        if (normalizedRut.length < 8) return false;
        const requestId = ++lookupRequestIdRef.current;
        try {
            const clients = await getClientsByRut(rutValue);
            if (requestId !== lookupRequestIdRef.current) return false;
            const client = Array.isArray(clients) && clients.length ? clients[clients.length - 1] : null;
            if (client) {
                const applied = await applyClientSelection(client, { source: lookupSource });
                if (!applied) return false;
                if (closeOnFound) {
                    onClose(false);
                }
                return true;
            }
            if (clearSelectionOnMiss) {
                setName('');
                setEmail('');
                setAddress('');
                setCity('');
                setPhone('');
            }
            if (showRegisterFieldsOnMiss) {
                setShowFields(true);
            }
            return false;
        } catch (error) {
            if (requestId === lookupRequestIdRef.current) {
                console.error(error);
            }
            return false;
        }
    }, [
        applyClientSelection,
        canReadClient,
        onClose,
        setAddress,
        setCity,
        setEmail,
        setName,
        setPhone,
    ]);

    const handleCancelSelection = useCallback((resetVarietyPrice = false) => {
        if (selectionInFlightRef.current || registrationInFlightRef.current) return;
        restoreCommittedClientFields();
        setShowFields(false);
        setSelectingTypeOnly(false);
        setTypeError('');
        if (resetVarietyPrice) setModalVarietyPrice(null);
        onClose(false);
    }, [onClose, restoreCommittedClientFields, setModalVarietyPrice]);

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        if (!canCreateClient) return;
        if (registrationInFlightRef.current) return;
        registrationInFlightRef.current = true;
        setRegistrationInFlight(true);

        try {
            const response = await registerClient({ name, rut, email, address, city, phone });
            const client = response.client;

            if (client) {
                const applied = await applyClientSelection(client, { source: 'register' });
                if (applied) onClose(false);
            }
        } catch (error) {
            console.error(error);
        } finally {
            registrationInFlightRef.current = false;
            setRegistrationInFlight(false);
        }
    };

    const handleDetailSell = async () => {
        if (selectionInFlightRef.current || registrationInFlightRef.current) return;
        if (allowContinueWithoutClient) {
            if (!selectingTypeOnly) {
                setSelectingTypeOnly(true);
                return;
            }
            if (!setClientType || !clientType) {
                setTypeError('Selecciona un formato para continuar sin cliente.');
                return;
            }
            setTypeError('');
            setRut("");
            onClose(false);
            return;
        }
        setRut("");
        onClose(false);
    }

    const handleSearchByRut = async () => {
        await lookupClientByRut(rut, {
            showRegisterFieldsOnMiss: true,
            closeOnFound: true,
            clearSelectionOnMiss: false,
            lookupSource: 'manual-search',
        });
    };

    useEffect(() => {
        if (isOpen) return;
        if (searchDebounceRef.current) {
            clearTimeout(searchDebounceRef.current);
            searchDebounceRef.current = null;
        }
        lastLookupRutRef.current = '';
        lookupRequestIdRef.current += 1;
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen || !canReadClient) return undefined;
        const normalizedRut = normalizeRutForLookup(rut);
        if (normalizedRut.length < 8) {
            lastLookupRutRef.current = '';
            return undefined;
        }
        if (lastLookupRutRef.current === normalizedRut) {
            return undefined;
        }
        if (searchDebounceRef.current) {
            clearTimeout(searchDebounceRef.current);
        }
        searchDebounceRef.current = setTimeout(() => {
            lastLookupRutRef.current = normalizedRut;
            lookupClientByRut(rut, {
                showRegisterFieldsOnMiss: true,
                closeOnFound: false,
                clearSelectionOnMiss: true,
                lookupSource: 'auto',
            });
        }, 350);
        return () => {
            if (searchDebounceRef.current) {
                clearTimeout(searchDebounceRef.current);
            }
        };
    }, [isOpen, rut, canReadClient, lookupClientByRut]);

    if (!isOpen) {
        return null;
    }

    const clientActionInFlight = selectionInFlight || registrationInFlight;

    // if (clientExists) {
    //     return (
    //         <div className={`fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 ${isOpen ? '' : 'hidden'
    //             }`}>
    //                 <div className="bg-white w-full md:w-1/2 rounded-lg">
    //                 <div
    //                     className="p-4 bg-gray-200 rounded-t-lg"
    //                 >
    //                     <h2>Cliente Encontrado</h2>
    //                 </div>
    //                 <div className='p-6'>
    //                     <p>{name}</p>
    //                     <p>{rut}</p>
    //                     <p>{email}</p>
    //                     <p>{address}</p>
    //                     <p>{city}</p>
    //                     <p>{phone}</p>
    //                 </div>
    //             </div>
    //         </div>
    //     )
    // }

    return (
        <div
            className={`fixed inset-0 z-[1000] flex items-start justify-center bg-black/50 px-4 py-6 overflow-y-auto ${isOpen ? '' : 'hidden'}`}
            onClick={(event) => event.stopPropagation()}
        >
            <div
                className="bg-white w-full max-w-3xl rounded-2xl shadow-2xl border border-gray-100 overflow-hidden max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-start justify-between px-6 py-4 border-b border-gray-100 bg-gray-50">
                    <div>
                        <p className="text-xs uppercase tracking-wide text-gray-500">Cliente</p>
                        <h2 className="text-lg font-semibold text-gray-900">Registrar cliente</h2>
                        <p className="text-xs text-gray-500 mt-1">
                            Busca por RUT o ingresa la información del cliente.
                        </p>
                    </div>
                    <button
                        onClick={() => handleCancelSelection(false)}
                        disabled={clientActionInFlight}
                        className={`text-gray-600 bg-white border border-gray-200 rounded-full w-9 h-9 flex items-center justify-center ${
                            clientActionInFlight ? 'cursor-not-allowed opacity-50' : 'hover:text-gray-900'
                        }`}
                    >
                        ✕
                    </button>
                </div>

                <div className="p-6 space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="md:col-span-2">
                            <label className="text-xs font-semibold text-gray-600 uppercase">RUT</label>
                            <input
                                type="text"
                                placeholder="12.345.678-9"
                                value={rut}
                                onChange={formatRut}
                                autoComplete="off"
                                className="mt-2 p-3 border border-gray-300 rounded-lg w-full"
                            />
                        </div>

                        {allowContinueWithoutClient && selectingTypeOnly && (
                            <div className="md:col-span-2 flex flex-col gap-1">
                                <label className="text-xs font-semibold text-gray-600 uppercase">Tipo de cliente</label>
                                <select
                                    className="p-3 border border-gray-300 rounded-lg"
                                    value={clientType || ''}
                                    onChange={(e) => {
                                        setTypeError('');
                                        setClientType && setClientType(e.target.value);
                                    }}
                                >
                                    <option value="">Seleccionar</option>
                                    {resolvedClientTypeOptions.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                                {typeError && <p className="text-xs text-red-600">{typeError}</p>}
                            </div>
                        )}
                    </div>

                    {showFields && (
                        <div className="space-y-4">
                            {!canCreateClient && (
                                <p className="text-sm text-red-600">
                                    No tienes permisos para registrar clientes.
                                </p>
                            )}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="md:col-span-2">
                                    <label className="text-xs font-semibold text-gray-600 uppercase">Nombre</label>
                                    <input
                                        type="text"
                                        placeholder="Nombre"
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        disabled={!rut}
                                        autoComplete="off"
                                        className="mt-2 p-3 border border-gray-300 rounded-lg w-full"
                                    />
                                </div>
                                <div>
                                    <label className="text-xs font-semibold text-gray-600 uppercase">Email</label>
                                    <input
                                        type="email"
                                        placeholder="Email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        autoComplete="off"
                                        disabled={!rut}
                                        className="mt-2 p-3 border border-gray-300 rounded-lg w-full"
                                    />
                                </div>
                                <div>
                                    <label className="text-xs font-semibold text-gray-600 uppercase">Teléfono</label>
                                    <input
                                        type="text"
                                        placeholder="Teléfono"
                                        value={phone}
                                        onChange={(e) => setPhone(e.target.value)}
                                        disabled={!rut}
                                        className="mt-2 p-3 border border-gray-300 rounded-lg w-full"
                                    />
                                </div>
                                <div className="md:col-span-2">
                                    <label className="text-xs font-semibold text-gray-600 uppercase">Dirección</label>
                                    <input
                                        type="text"
                                        placeholder="Dirección"
                                        value={address}
                                        onChange={(e) => setAddress(e.target.value)}
                                        disabled={!rut}
                                        className="mt-2 p-3 border border-gray-300 rounded-lg w-full"
                                    />
                                </div>
                                <div className="md:col-span-2">
                                    <label className="text-xs font-semibold text-gray-600 uppercase">Ciudad</label>
                                    <input
                                        type="text"
                                        placeholder="Ciudad"
                                        value={city}
                                        onChange={(e) => setCity(e.target.value)}
                                        disabled={!rut}
                                        className="mt-2 p-3 border border-gray-300 rounded-lg w-full"
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 border-t border-gray-100 pt-4">
                        {!requireClient && (
                            <button
                                disabled={clientActionInFlight}
                                className={`px-4 py-2 text-white rounded-lg ${
                                    clientActionInFlight
                                        ? 'cursor-not-allowed bg-gray-500'
                                        : 'bg-blue-600 hover:bg-blue-700'
                                }`}
                                onClick={handleDetailSell}
                            >
                                {allowContinueWithoutClient
                                    ? (selectingTypeOnly ? 'Confirmar sin cliente' : 'Continuar sin cliente')
                                    : detailActionLabel}
                            </button>
                        )}
                        <div className="flex flex-wrap gap-2">
                            <button
                                disabled={clientActionInFlight}
                                className={`px-4 py-2 rounded-lg border border-gray-200 ${
                                    clientActionInFlight
                                        ? 'cursor-not-allowed text-gray-400 opacity-60'
                                        : 'text-gray-700 hover:text-gray-900'
                                }`}
                                onClick={() => handleCancelSelection(true)}
                            >
                                {cancelLabel}
                            </button>
                            {showFields ? (
                                canCreateClient && (
                                    <button
                                        disabled={clientActionInFlight}
                                        className={`px-4 py-2 text-white rounded-lg ${
                                            clientActionInFlight
                                                ? 'cursor-not-allowed bg-gray-500'
                                                : 'bg-blue-600 hover:bg-blue-700'
                                        }`}
                                        onClick={handleFormSubmit}
                                    >
                                        Registrar
                                    </button>
                                )
                            ) : (
                                <button
                                    className={`px-4 py-2 text-white rounded-lg ${
                                        rut.length < 8 || !canReadClient || clientActionInFlight
                                            ? "cursor-not-allowed bg-gray-500"
                                            : "bg-blue-600 hover:bg-blue-700"
                                    }`}
                                    onClick={handleSearchByRut}
                                    disabled={rut.length < 8 || !canReadClient || clientActionInFlight}
                                >
                                    Buscar
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default ModalClientRegistration;
